import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
  DELIMITER_SETTING_CREATE,
  DELIMITER_SETTING_CREATE_COMPLETED,
  DELIMITER_SETTING_CREATE_VIEW,
  DELIMITER_SETTING_EDIT_MODE,
  DELIMITER_SETTING_FIND,
  DELIMITER_SETTING_FIND_ALL,
  DELIMITER_SETTING_FIND_ALL_SUCCESS,
  DELIMITER_SETTING_FIND_SUCCESS,
  DELIMITER_SETTING_REMOVE,
  DELIMITER_SETTING_REMOVE_COMPLETED,
  DELIMITER_SETTING_RESET_ERROR,
  DELIMITER_SETTING_UPDATE,
  DELIMITER_SETTING_UPDATE_COMPLETED,
  DELIMITER_SETTING_UPDATE_ID,
  DELIMITER_SETTING_UPDATE_LOADING,
  DELIMITER_SETTING_UPDATE_VIEW,
  DELIMITER_SETTING_CHANGE,
} from '@store/actions';
import { ConfiguracionDelimitador, ConfiguracionDelimitadorForm } from '@core/models';
import { AccountService, AuditService, AuthService, DelimiterSettingService } from '@core/services';
import { selectAccountId, selectCredential, selectDelimiterSettingForm } from '@store/selectors';
import { AuditTypeEnum, PlatformAudit, RoleEnum, RolModuleEnum } from '@core/enums';

@Injectable()
export class DelimiterSettingEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private delimiterSettingService: DelimiterSettingService,
    private accountService: AccountService,
    private authService: AuthService,
    private auditService: AuditService,
  ) {}

   createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_CREATE_VIEW),
      mergeMap(() => {
        return [
          DELIMITER_SETTING_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_UPDATE_VIEW),
      mergeMap(({ delimiterSettingId }) => {
        return [
          DELIMITER_SETTING_UPDATE_ID({ delimiterSettingId }),
          DELIMITER_SETTING_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(DELIMITER_SETTING_UPDATE_LOADING({  loading: true }));

        return this.delimiterSettingService.find(id).pipe(
          mergeMap(({ data }) => {
            return [
              DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
              DELIMITER_SETTING_FIND_SUCCESS({ delimiterSetting: data }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_FIND_ALL),
      filter(() => this.authService.hasRole(RolModuleEnum.Role)),
      mergeMap(() => {
        this.store.dispatch(DELIMITER_SETTING_UPDATE_LOADING({  loading: true }));

        return this.delimiterSettingService.findAll().pipe(
          mergeMap(({ data }) => {
            const delimiterSettingList = data as ConfiguracionDelimitador[];

            return [
              DELIMITER_SETTING_FIND_ALL_SUCCESS({ delimiterSettingList }),
              DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_CREATE),
      withLatestFrom(
        this.store.select(selectDelimiterSettingForm),
      ),
      filter(([_, roleForm ]) =>  !!roleForm),
      map(([_, roleForm]) => ({ roleForm: roleForm as ConfiguracionDelimitadorForm })),
      mergeMap(({ roleForm }) => {
        this.store.dispatch(DELIMITER_SETTING_UPDATE_LOADING({  loading: true }));

        return this.delimiterSettingService.create(roleForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Delimiter setting successfully created' } }),
              DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
              DELIMITER_SETTING_CHANGE(),
              DELIMITER_SETTING_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_RESET_ERROR(),
                  DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_CREATE_COMPLETED),
      tap(() => {
        this.delimiterSettingService.creationCompleted();
        this.accountService.closeDialog();
      })
    ),
    { dispatch: false }
  )

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_UPDATE),
      withLatestFrom(
        this.store.select(selectAccountId),
        this.store.select(selectDelimiterSettingForm),
      ),
      filter(([_, id, attrs ]) =>  !!id && !!attrs),
      map(([_, id, attrs]) => ({ id: id as string, attrs: attrs as ConfiguracionDelimitadorForm })),
      mergeMap(({ id, attrs }) => {
        this.store.dispatch(DELIMITER_SETTING_UPDATE_LOADING({  loading: true }));

        return this.delimiterSettingService.update(id, attrs).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Delimiter setting successfully updated' } }),
              DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
              DELIMITER_SETTING_CHANGE(),
              DELIMITER_SETTING_UPDATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_RESET_ERROR(),
                  DELIMITER_SETTING_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_UPDATE_COMPLETED),
      tap(() => {
        this.delimiterSettingService.updateCompleted();
        this.accountService.closeDialog();
      })
    ),
    { dispatch: false }
  )

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_REMOVE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.delimiterSettingService.remove(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Delimiter setting successfully removed' } }),
              DELIMITER_SETTING_REMOVE_COMPLETED(),
              DELIMITER_SETTING_FIND_ALL(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  removeComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_REMOVE_COMPLETED),
      tap(() => {})
    ),
    { dispatch: false }
  )


  audit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELIMITER_SETTING_CHANGE),
      withLatestFrom(
        this.store.select(selectCredential),
        this.store.select(selectDelimiterSettingForm),
      ),
      filter(([_, credential, delimiterSettingForm ]) =>  !!credential && !!delimiterSettingForm),
      mergeMap(([_, credential, delimiterSettingForm]) => {
        const { leadingCharacter, trailingCharacter, accountId } = delimiterSettingForm as ConfiguracionDelimitadorForm;

        return this.auditService.create({
            isFrontEnd: true,
            platform: PlatformAudit.JuvOffice,
            positionName: RoleEnum.SuperAdmin,
            type: AuditTypeEnum.Information,
            accountId: accountId,
            record: `Leading Character and/or Trailing Character settings for gift cards were modified by (${credential?.idUsuario}) ${credential?.nombre}. This change affects the reading of all gift cards.`,
            extra: {
              leadingCharacter: leadingCharacter,
              trailingCharacter: trailingCharacter,
              accountId: accountId,
              userId: credential?.idUsuario,
              userName: credential?.nombre,
            },
          }).pipe(
          mergeMap(() => {
            return [];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  DELIMITER_SETTING_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );
}
